import { useMutation } from '@apollo/client';
import { AnalyticIdentifier } from '@common/neo.tax';
import * as Sentry from '@sentry/react';
import { Button, GeneralFormError, AlertModalLayout, ControlledInput, Modal } from '@ui-components/neo.tax';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import {
  REMOVE_USER_ACCESS_FROM_ORGANIZATION,
  RemoveUserAccessFromOrganizationMutationVariables,
  RemoveUserAccessFromOrganizationMutation,
} from '@neo.tax/shared/graphql';
import { useAnalyticId } from '@neo.tax/shared/state';
import { OrganizationMemberTableItemMember } from '@neo.tax/shared/types';

import { DeleteMemberFormValues } from './types';

interface DeleteMemberModalProps {
  organizationId: string;
  onDelete: () => void;
  member: OrganizationMemberTableItemMember | undefined;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

export const DeleteMemberModal = ({
  isOpen,
  member,
  onDelete,
  organizationId,
  setIsOpen,
}: DeleteMemberModalProps): React.ReactElement | null => {
  const fullNameInputId = useAnalyticId(AnalyticIdentifier.RTC_DELETE_MEMBER_NAME_INPUT);
  const [errorMessage, setErrorMessage] = useState<string>();

  const { control, formState, handleSubmit, reset } = useForm<DeleteMemberFormValues>({
    mode: 'onChange',
    defaultValues: {
      fullName: '',
    },
  });

  const [removeUserAccessFromOrganization, { loading: removeUserAccessFromOrganizationLoading }] = useMutation<
    RemoveUserAccessFromOrganizationMutation,
    RemoveUserAccessFromOrganizationMutationVariables
  >(REMOVE_USER_ACCESS_FROM_ORGANIZATION);

  const onSubmit = async (): Promise<void> => {
    if (!member?.id) return;
    try {
      await removeUserAccessFromOrganization({
        variables: { organizationId, userId: member.id },
        update: (cache) => {
          cache.modify({
            id: `User:${member.id}`,
            fields: (_, { DELETE }) => DELETE,
          });
        },
        onCompleted: onDelete,
      });
      setIsOpen(false);
    } catch (e) {
      setErrorMessage('Something went wrong. Please try again.');
      Sentry.captureException(e);
    }
  };

  const onClickCancel = (): void => {
    setIsOpen(false);
    reset();
  };

  if (!member) return null;
  return (
    <Modal isOpen={!!isOpen} setIsOpen={setIsOpen}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <AlertModalLayout
          title={`Delete user "${member.fullName}"`}
          copy={`Are you sure you want to delete ${member.fullName}? They will be permanently removed.`}
          trailingContent={
            <>
              <Button size="base" variant="white" rounded="regular" onClick={onClickCancel}>
                Cancel
              </Button>
              <Button
                disabled={!formState.isValid || removeUserAccessFromOrganizationLoading}
                rounded="regular"
                size="base"
                type="submit"
                variant="rsbry"
              >
                {removeUserAccessFromOrganizationLoading ? 'Deleting Member...' : 'Delete'}
              </Button>
            </>
          }
        >
          <ControlledInput
            control={control}
            name="fullName"
            rules={{ required: true, validate: (fullName: string) => fullName === member.fullName }}
            id={fullNameInputId}
            label="To delete, please type the user name:"
          />
          <GeneralFormError centered error={!!errorMessage}>
            {errorMessage}
          </GeneralFormError>
        </AlertModalLayout>
      </form>
    </Modal>
  );
};
